import { EInvoiceAddressSchemeType, EntityType, isValidEInvoiceAddressFormat } from '@components'

import { TFunction } from 'i18next'
import isEmpty from 'lodash/isEmpty'
import { z } from 'zod'

import { getRegistrationNumberFieldInvalidFormatErrorMessage } from '@features/invoices/utils/getRegistrationNumberFieldInvalidFormatErrorMessage'
import { getVatNumberFieldInvalidFormatErrorMessage } from '@features/invoices/utils/getVatNumberFieldInvalidFormatErrorMessage'

import { getRegistrationNumberRegex, getVatNoRegex, isValidEmail } from '../../../../../../utils'
import { ZervantLocale } from '../../../../../invoices/enums/zervantLocale'
import { CustomerType } from '../../../../enums/customerType'
import { CustomerFormSchema } from '../../types/customerForm/customerFormSchema'
import { getEInvoiceAddressFieldInvalidFormatErrorMessage } from './getEInvoiceAddressFieldInvalidFormatErrorMessage'

export const getCustomerFormSchema = (isFrenchComplianceMode = false, t: TFunction): CustomerFormSchema => {
  const eInvoiceAddressSchema = z.object({
    address: z
      .string()
      .trim()
      .transform((address) => address.replace(/\s/g, ''))
      .optional(),
    networkOperatorId: z.string().optional(),
    networkType: z.string().optional(),
    scheme: z.nativeEnum(EInvoiceAddressSchemeType).optional(),
  })

  const customerAddressSchema = z.object({
    city: z.string().optional(),
    country: z.string().optional(),
    pobox: z.string().optional(),
    streetAddress1: z.string().optional(),
    streetAddress2: z.string().optional(),
  })

  return z
    .object({
      address: customerAddressSchema.optional(),
      autoCompletedFields: z.record(z.string(), z.boolean()).optional(),
      businessId: z.string().trim().optional(),
      contactFirstName: z.string().optional(),
      contactLastName: z.string().optional(),
      contactEmail: z
        .string()
        .refine((value) => !value || isValidEmail(value), {
          message: t('customer_modal.form.validation.invalid_email'),
        })
        .optional(),
      contactPhoneNumber: z.string().optional(),
      contactPersons: z
        .array(
          z.object({
            email: z
              .string()
              .refine((value) => !value || isValidEmail(value), {
                message: t('customer_modal.form.validation.invalid_email'),
              })
              .optional(),
            firstName: z.string().optional(),
            isPrimary: z.boolean().optional(),
            lastName: z.string().optional(),
          }),
        )
        .default([]),
      companyName: z.string().optional(),
      currency: z.string().optional(),
      customerNumber: z.number().optional(),
      customerType: z.nativeEnum(CustomerType),
      email: z
        .string()
        .refine((value) => !value || isValidEmail(value), {
          message: t('customer_modal.form.validation.invalid_email'),
        })
        .optional(),
      entityType: z.nativeEnum(EntityType).optional(),
      eInvoiceAddress: eInvoiceAddressSchema.optional(),
      firstName: z.string().optional(),
      id: z.string().optional(),
      isAddressExempt: z.boolean().optional(),
      isContactPrimary: z.boolean().optional(),
      language: z.nativeEnum(ZervantLocale).optional(),
      lastName: z.string().optional(),
      paymentTerm: z.number().optional(),
      phone: z.string().optional(),
      shippingAddress: customerAddressSchema.optional(),
      vatNumber: z.string().trim().optional(),
    })
    .refine((data) => !(data.customerType === CustomerType.Company && isEmpty(data.companyName)), {
      path: ['companyName'],
      message: t('customer_modal.form.validation.company_name_required'),
    })
    .refine((data) => !(data.customerType === CustomerType.Individual && isEmpty(data.firstName)), {
      path: ['firstName'],
      message: t('customer_modal.form.validation.first_name_required'),
    })
    .refine((data) => !(data.customerType === CustomerType.Individual && isEmpty(data.lastName)), {
      path: ['lastName'],
      message: t('customer_modal.form.validation.last_name_required'),
    })
    .superRefine((data, ctx) => {
      const countryValue = data.address?.country

      // SIRET / registration number and TVA / VAT are validated against the customer's own country.
      // The regex helpers return /.*/ for countries without a specific format, so this is a no-op
      // for unsupported countries and does not depend on the org's compliance mode.
      if (data.businessId && !getRegistrationNumberRegex(countryValue).test(data.businessId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['businessId'],
          message: getRegistrationNumberFieldInvalidFormatErrorMessage(countryValue, t),
        })
      }

      if (data.vatNumber && !getVatNoRegex(countryValue).test(data.vatNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['vatNumber'],
          message: getVatNumberFieldInvalidFormatErrorMessage(countryValue, t),
        })
      }

      // The e-invoicing section is hidden for a French-compliance org with a non-French customer
      // (see CompanyCustomerModalBody). Validate the address exactly when the section is shown: the
      // French org skips it outside France, while the other markets (DE, NL, DK) keeps their existing validation.
      const isEInvoiceSectionShown = !isFrenchComplianceMode || countryValue === 'FR'

      if (!isEInvoiceSectionShown) {
        return
      }

      const addressValue = data.eInvoiceAddress?.address
      const scheme = data.eInvoiceAddress?.scheme

      if (scheme && addressValue && !isValidEInvoiceAddressFormat(addressValue, scheme, countryValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['eInvoiceAddress', 'address'],
          message: getEInvoiceAddressFieldInvalidFormatErrorMessage(scheme, t),
        })
      }
    })
}
